import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  ShieldCheck, RefreshCw, ChevronLeft, ChevronRight, Filter,
  CheckCircle2, XCircle, Lock, LogIn, LogOut, KeyRound, ShieldAlert,
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 50;

const EVENT_META = {
  login_success:           { label: 'Login success',       Icon: LogIn,        cls: 'text-emerald-700 bg-emerald-50' },
  login_failure:           { label: 'Login failure',       Icon: XCircle,      cls: 'text-rose-700 bg-rose-50' },
  login_locked:            { label: 'Account locked',      Icon: Lock,         cls: 'text-amber-800 bg-amber-50' },
  logout:                  { label: 'Logout',              Icon: LogOut,       cls: 'text-slate-700 bg-slate-50' },
  password_change:         { label: 'Password change',     Icon: KeyRound,     cls: 'text-indigo-700 bg-indigo-50' },
  password_reset_request:  { label: 'Reset requested',     Icon: ShieldAlert,  cls: 'text-amber-700 bg-amber-50' },
  password_reset_success:  { label: 'Reset success',       Icon: CheckCircle2, cls: 'text-emerald-700 bg-emerald-50' },
  password_reset_failure:  { label: 'Reset failure',       Icon: XCircle,      cls: 'text-rose-700 bg-rose-50' },
  security_questions_set:  { label: 'Sec. Qs configured',  Icon: ShieldCheck,  cls: 'text-blue-700 bg-blue-50' },
};

export const AuditLog = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [summary, setSummary] = useState([]);
  const [filterEvent, setFilterEvent] = useState('');
  const [filterEmail, setFilterEmail] = useState('');
  const [filterSuccess, setFilterSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = async (newSkip = 0) => {
    setLoading(true); setError('');
    try {
      const params = { limit: PAGE_SIZE, skip: newSkip };
      if (filterEvent) params.event_type = filterEvent;
      if (filterEmail.trim()) params.user_email = filterEmail.trim();
      if (filterSuccess !== '') params.success = filterSuccess === 'true';
      const { data } = await axios.get(`${API}/admin/audit`, { params });
      setRows(data.rows || []);
      setTotal(data.total || 0);
      setSkip(newSkip);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/audit/summary`, { params: { days: 7 } });
      setSummary(data.summary || []);
    } catch (e) { /* non-critical */ }
  };

  useEffect(() => { load(0); loadSummary(); /* eslint-disable-next-line */ }, []);

  const onFilter = (e) => { e.preventDefault(); load(0); };

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-primary flex items-center gap-2">
            <ShieldCheck className="h-6 w-6" /> Audit Log
          </h1>
          <p className="text-sm text-text-muted mt-1">
            Append-only record of all auth events. Auto-purged after 90 days.
          </p>
        </div>
        <Button onClick={() => { load(skip); loadSummary(); }} variant="outline" size="sm" data-testid="audit-refresh">
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </header>

      {/* 7-day summary chips */}
      {summary.length > 0 && (
        <div className="bg-white border border-border rounded-md p-4">
          <h2 className="text-xs uppercase tracking-wider text-text-secondary font-semibold mb-3">
            Last 7 days
          </h2>
          <div className="flex flex-wrap gap-2" data-testid="audit-summary">
            {summary.map((s, i) => {
              const meta = EVENT_META[s.event_type] || { label: s.event_type, Icon: ShieldCheck, cls: 'text-slate-700 bg-slate-50' };
              const Icon = meta.Icon;
              return (
                <span
                  key={i}
                  className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md border border-border ${meta.cls}`}
                  data-testid={`audit-chip-${s.event_type}-${s.success ? 'ok' : 'fail'}`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {meta.label}
                  {!s.success && <span className="opacity-70">· fail</span>}
                  <span className="font-bold">{s.count}</span>
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Filters */}
      <form onSubmit={onFilter} className="bg-white border border-border rounded-md p-4 flex flex-wrap items-end gap-3" data-testid="audit-filters">
        <div className="flex-1 min-w-[150px]">
          <label className="block text-[10px] uppercase tracking-wider text-text-secondary font-semibold mb-1">Event</label>
          <select
            value={filterEvent}
            onChange={(e) => setFilterEvent(e.target.value)}
            className="w-full border border-border rounded px-2 py-1.5 text-sm"
            data-testid="audit-filter-event"
          >
            <option value="">All events</option>
            {Object.entries(EVENT_META).map(([k, m]) => (
              <option key={k} value={k}>{m.label}</option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[180px]">
          <label className="block text-[10px] uppercase tracking-wider text-text-secondary font-semibold mb-1">Email</label>
          <input
            value={filterEmail}
            onChange={(e) => setFilterEmail(e.target.value)}
            placeholder="user@example.com"
            className="w-full border border-border rounded px-2 py-1.5 text-sm"
            data-testid="audit-filter-email"
          />
        </div>
        <div className="min-w-[100px]">
          <label className="block text-[10px] uppercase tracking-wider text-text-secondary font-semibold mb-1">Status</label>
          <select
            value={filterSuccess}
            onChange={(e) => setFilterSuccess(e.target.value)}
            className="w-full border border-border rounded px-2 py-1.5 text-sm"
            data-testid="audit-filter-success"
          >
            <option value="">Any</option>
            <option value="true">Success</option>
            <option value="false">Failure</option>
          </select>
        </div>
        <Button type="submit" size="sm" data-testid="audit-filter-apply">
          <Filter className="h-3.5 w-3.5 mr-1" /> Apply
        </Button>
      </form>

      {/* Table */}
      <div className="bg-white border border-border rounded-md overflow-hidden">
        <div className="px-4 py-2 bg-background-secondary text-[11px] font-semibold uppercase tracking-wider text-text-secondary flex justify-between">
          <span>Events ({total.toLocaleString()})</span>
          <span className="text-text-muted">Showing {rows.length} · page {Math.floor(skip / PAGE_SIZE) + 1}</span>
        </div>
        {error && (
          <div className="p-4 text-sm text-state-error bg-rose-50 border-b border-border" data-testid="audit-error">{error}</div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-background-secondary text-text-secondary">
              <tr>
                <th className="text-left px-3 py-1.5 font-semibold">Time</th>
                <th className="text-left px-3 py-1.5 font-semibold">Event</th>
                <th className="text-left px-3 py-1.5 font-semibold">User</th>
                <th className="text-left px-3 py-1.5 font-semibold">IP</th>
                <th className="text-left px-3 py-1.5 font-semibold">Result</th>
                <th className="text-left px-3 py-1.5 font-semibold">Details</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading && (
                <tr><td colSpan="6" className="text-center py-12 text-text-muted">No audit events match the current filters.</td></tr>
              )}
              {rows.map((r) => {
                const meta = EVENT_META[r.event_type] || { label: r.event_type, Icon: ShieldCheck, cls: 'text-slate-700 bg-slate-50' };
                const Icon = meta.Icon;
                return (
                  <tr key={r.id} className="border-b border-border last:border-b-0 hover:bg-background-secondary" data-testid={`audit-row-${r.event_type}`}>
                    <td className="px-3 py-1.5 whitespace-nowrap text-text-secondary font-mono">{formatTs(r.timestamp)}</td>
                    <td className="px-3 py-1.5">
                      <span className={`inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded font-semibold ${meta.cls}`}>
                        <Icon className="h-3 w-3" /> {meta.label}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-text-primary">{r.user_email || <span className="text-text-muted">—</span>}</td>
                    <td className="px-3 py-1.5 font-mono text-[10px] text-text-muted">{r.ip_address || '—'}</td>
                    <td className="px-3 py-1.5">
                      <span className={`px-1.5 py-0.5 rounded font-semibold text-[10px] ${
                        r.success ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
                      }`}>
                        {r.success ? 'OK' : 'FAIL'}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-text-muted text-[10px] max-w-[240px] truncate" title={JSON.stringify(r.details)}>
                      {detailsSummary(r.details)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="px-3 py-2 bg-background-secondary border-t border-border flex items-center justify-between">
          <Button
            onClick={() => load(Math.max(0, skip - PAGE_SIZE))}
            disabled={skip === 0 || loading}
            variant="outline" size="sm"
            data-testid="audit-prev"
          >
            <ChevronLeft className="h-3.5 w-3.5" /> Prev
          </Button>
          <span className="text-[11px] text-text-muted">{skip + 1}–{Math.min(skip + rows.length, total)} of {total.toLocaleString()}</span>
          <Button
            onClick={() => load(skip + PAGE_SIZE)}
            disabled={skip + rows.length >= total || loading}
            variant="outline" size="sm"
            data-testid="audit-next"
          >
            Next <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const formatTs = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { year: '2-digit', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch { return iso; }
};

const detailsSummary = (d) => {
  if (!d || typeof d !== 'object') return '';
  const keys = Object.keys(d);
  if (keys.length === 0) return '';
  return keys.slice(0, 3).map((k) => `${k}=${d[k]}`).join(' · ');
};

export default AuditLog;
